<template>
  <FishScene cena="agua-doce" :escurecimento="0.4" class="cais">
    <GameAlert
      :show="!!alertConfig.message"
      :type="alertConfig.type"
      :message="alertConfig.message"
      @close="closeAlert"
    />

    <div class="cais__interior">
      <header class="cais__topo">
        <h1 class="cais__titulo banner-titulo">{{ $t('cais.titulo') }}</h1>
        <p class="cais__saudacao">{{ $t('cais.saudacao') }}</p>
      </header>

      <!--
        D13 — a Home é o CAIS: o hub diário do jogador, não uma landing page.

        A versão anterior desta tela era material de divulgação — cards de rede social e três
        explicações de bônus de indicação — mostrado a quem JÁ É jogador, todo dia. Cada coisa
        aqui agora é um objeto do cais: as missões numa tábua, as notícias pregadas no mural, a
        indicação dentro de uma garrafa. E uma ação principal só, na base (D6, D11).
      -->

      <!-- A tábua é o quadro de Missões Diárias sem o botão de resgate: as mesmas linhas
           (LinhaDeMissao), na mesma ordem, e o mesmo prêmio (PremioDasMissoes). Ela chegou a
           cortar em três quando o dia tinha até dez missões — 3 de 7 parecendo o dia todo. Com o
           dia em 3 a 5 (11/09/2026) cabe inteiro, e não há mais recorte a explicar. -->
      <section v-if="missions.length" class="cais__tabua papel-bloco" aria-labelledby="cais-missoes">
        <div class="cais__tabua-topo">
          <h2 id="cais-missoes" class="cais__rotulo">{{ $t('cais.missoes') }}</h2>
          <span class="cais__tabua-placar t-numero">{{ missoesFeitas }}/{{ missions.length }}</span>
        </div>
        <ul class="cais__missoes">
          <LinhaDeMissao v-for="missao in missions" :key="missao.id" :missao="missao" />
        </ul>
        <PremioDasMissoes v-if="rewardFormatted" class="cais__tabua-premio" :premio="rewardFormatted" compacto />
        <button type="button" class="cais__tabua-link" @click="abrirQuadroDeMissoes">
          {{ $t('cais.abrirMissoes') }}
        </button>
      </section>

      <!--
        A garrafa é um OBJETO do cais, e fica ACIMA do mural.

        Ela era um link de texto no rodapé, entre o mural e as redes sociais: fonte de apoio,
        sublinhado, sem superfície nenhuma, e sobre a água escura com 1px de sombra. Era a
        coisa que mais faz o jogo crescer, escondida no lugar onde menos se olha e ilegível
        onde se olhava. Aqui ela tem papel embaixo, a garrafa em 64px (2× do sprite, sem
        borrar) e diz o que paga — sem virar a segunda ação principal da tela (D6): o botão é
        `neutro`, e o `primario` continua sendo um só, o Pescar da base.
      -->
      <section v-if="playerId" class="cais__convite repouso-brilha" aria-labelledby="cais-convite">
        <span class="cais__convite-selo" aria-hidden="true">
          <FishIcon nome="cais-garrafa" tamanho="enorme" class="repouso-flutua" />
        </span>
        <div class="cais__convite-texto">
          <p class="cais__convite-chamada">{{ $t('cais.garrafaChamada') }}</p>
          <h2 id="cais-convite" class="cais__convite-ganho">
            <span class="cais__convite-numero">{{ $t('cais.garrafaNumero') }}</span>
            <span class="cais__convite-unidade">
              <FishIcon nome="gem" tamanho="pequeno" />
              {{ $t('cais.garrafaUnidade') }}
            </span>
          </h2>
          <p class="cais__convite-regra">{{ $t('cais.garrafaRegra') }}</p>
        </div>
        <FishButton
          class="cais__convite-acao"
          variante="neutro"
          icone="share-alt"
          @click="garrafaAberta = true"
        >{{ $t('share.inviteFriends') }}</FishButton>
      </section>

      <section class="cais__mural" aria-labelledby="cais-mural">
        <h2 id="cais-mural" class="cais__rotulo cais__rotulo--claro">{{ $t('cais.mural') }}</h2>

        <p v-if="loading" class="cais__mural-estado">{{ $t('announcements.loading') }}</p>
        <p v-else-if="error" class="cais__mural-estado">{{ $t('announcements.error') }}</p>

        <ul v-else-if="hasAnnouncements" class="cais__recados">
          <li
            v-for="(announcement, i) in announcements"
            :key="announcement.id"
            class="cais__recado"
            :style="{ '--gira': `${[-1.2, 0.9, -0.6][i % 3]}deg` }"
          >
            <div class="cais__recado-topo">
              <h3 class="cais__recado-titulo">{{ announcement.text }}</h3>
              <time class="cais__recado-data">{{ formatDate(announcement.endDate) }}</time>
            </div>
            <div
              class="cais__recado-corpo"
              v-html="formatDescription(announcement.description)"
            ></div>

            <!-- O "ler tudo" abre o recado num MODAL (pedido do autor, 11/09). A primeira porta de
                 saída do corte de seis linhas (03/09) abria o recado no lugar, e não servia: a folha
                 empilhada é um `background-size: 100% 100%` com padding em % da LARGURA, então num
                 recado alto a arte esticava e o texto vazava da folha de cima — e o anúncio longo
                 empurrava o "Fechar" para fora da tela. O mural fica sempre curto; quem lê, lê
                 no pergaminho, que tem rolagem própria. -->
            <button
              v-if="recadoLongo(announcement)"
              type="button"
              class="cais__recado-link"
              @click="lerRecado(announcement)"
            >{{ $t('announcements.readMore') }}</button>

            <!-- rel: sem ele, a página aberta pode reescrever esta pela window.opener. O esquema
                 do href é validado no servidor, que recusa o que não for http/https. -->
            <a
              v-if="announcement.link"
              :href="announcement.link"
              target="_blank"
              rel="noopener noreferrer"
              class="cais__recado-link"
            >{{ $t('announcements.readMore') }}</a>
          </li>
        </ul>
      </section>

      <footer class="cais__social">
        <span class="cais__social-rotulo">{{ $t('cais.encontreNos') }}</span>
        <a href="https://x.com/Unligamestudio" target="_blank" rel="noopener noreferrer" aria-label="Twitter"><i class="fa-brands fa-square-x-twitter"></i></a>
        <a href="https://www.youtube.com/@UnliGamesStudio" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><i class="fab fa-youtube"></i></a>
        <a href="https://www.instagram.com/unligames/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i class="fab fa-instagram"></i></a>
        <a href="https://www.linkedin.com/company/101185636" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><i class="fab fa-linkedin"></i></a>
        <a href="https://t.me/+byBMdvSfpEVmMTdh" target="_blank" rel="noopener noreferrer" aria-label="Telegram"><i class="fab fa-telegram"></i></a>
      </footer>
    </div>

    <!-- A ação do dia, na altura do polegar (D11). Lê o estado: com energia pesca, sem energia
         descansa. Uma coisa só, grande. -->
    <div class="cais__acao">
      <FishButton variante="primario" tamanho="grande" bloco @click="acaoPrincipal">
        {{ temEnergia ? $t('cais.pescar') : $t('cais.descansar') }}
      </FishButton>
    </div>

    <!-- O recado inteiro. O `recadoLido` sobrevive ao fechar: se ele fosse zerado junto, o
         título e o texto sumiriam durante o fade de saída do véu, e o modal piscaria vazio. -->
    <FishModal
      :aberto="leituraAberta"
      :titulo="recadoLido ? recadoLido.text : ''"
      :rotulo="$t('cais.mural')"
      :rotulo-fechar="$t('announcements.fechar')"
      @fechar="leituraAberta = false"
    >
      <template v-if="recadoLido">
        <time v-if="recadoLido.endDate" class="leitura__data">{{ formatDate(recadoLido.endDate) }}</time>
        <!-- A rolagem é do CORPO, não da caixa do modal: rolando a caixa inteira, o "X" do título
             subia junto e o jogador terminava de ler sem ter como fechar à vista. -->
        <div class="leitura__corpo" v-html="formatDescription(recadoLido.description)"></div>
        <a
          v-if="recadoLido.link"
          :href="recadoLido.link"
          target="_blank"
          rel="noopener noreferrer"
          class="leitura__link"
        >{{ recadoLido.link }}</a>
      </template>
      <template #acoes>
        <FishButton variante="neutro" @click="leituraAberta = false">{{ $t('announcements.fechar') }}</FishButton>
      </template>
    </FishModal>

    <!-- A garrafa (D13): a indicação mora DENTRO dela, sob demanda — não em três cards na porta.
         O título vai na PROP: o `#titulo` do FishModal não existe (só `titulo`, `acoes` e o
         default), e o slot que estava aqui era descartado em silêncio — o modal abria sem
         cabeçalho e sem o "X" de fechar. -->
    <FishModal
      :aberto="garrafaAberta"
      :titulo="$t('home.referral.title')"
      :rotulo="$t('cais.garrafa')"
      @fechar="garrafaAberta = false"
    >
      <!-- Cada bônus é uma ficha, com o selo do que ele paga. Em lista corrida os quatro viravam
           um parágrafo só e o olho não achava onde um acabava e o outro começava. -->
      <ul class="garrafa__bonus">
        <li
          v-for="(bonus, i) in bonusDaGarrafa"
          :key="bonus.chave"
          class="garrafa__ficha surge escalonado"
          :style="{ '--ordem': i }"
        >
          <span class="garrafa__selo" aria-hidden="true">
            <FishIcon :nome="bonus.icone" tamanho="medio" />
          </span>
          <span class="garrafa__ficha-texto">
            <strong class="garrafa__ficha-titulo">{{ $t(`home.referral.${bonus.chave}.title`) }}</strong>
            <span class="garrafa__ficha-desc" v-html="$t(`home.referral.${bonus.chave}.description`)"></span>
          </span>
        </li>
      </ul>

      <!-- O fio tracejado é o idioma do caderno: separa a promessa da ação sem inventar moldura. -->
      <div class="garrafa__partilha">
        <p class="garrafa__info">{{ $t('home.referral.shareInfo') }}</p>
        <div class="garrafa__linha">
          <input
            type="text"
            :value="shareUrl"
            readonly
            ref="shareInput"
            class="garrafa__link"
            :aria-label="$t('home.referral.shareInfo')"
            @click="copyToClipboard"
          >
          <FishButton
            tamanho="pequeno"
            :icone="linkCopiado ? 'check' : 'copy'"
            :aria-label="$t('share.copyLink')"
            @click="copyToClipboard"
          />
        </div>
        <ShareButton
          :share-url="shareUrl"
          :share-title="$t('home.referral.shareTitle')"
          :share-text="$t('home.referral.shareText', { level: playerLevel })"
        />
      </div>
    </FishModal>
  </FishScene>
</template>

<script>
import ShareButton from '@/components/share/ShareButton.vue';
import GameAlert from '@/shared/common/Alert.vue';
import { FishButton, FishModal, FishIcon, FishScene } from '@/shared/ui';
import { mapState, mapActions, mapGetters } from 'vuex';
import { useI18n } from 'vue-i18n';
import { formatarDescricao } from '@/utils/announcementText';
import LinhaDeMissao from '@/shared/components/LinhaDeMissao.vue';
import PremioDasMissoes from '@/shared/components/PremioDasMissoes.vue';

/**
 * Os quatro bônus da garrafa, na ordem em que o jogador os alcança: o amigo entra, chega ao
 * nível 5, continua subindo, e um dia compra. O texto mora no i18n; aqui fica só o selo — e o
 * nome do selo tem que existir no `pixel-icons.css`, senão o FishIcon renderiza vazio sem erro.
 */
const BONUS_DA_GARRAFA = [
  { chave: 'newRegistration', icone: 'user-plus' },
  { chave: 'level5', icone: 'level-up-alt' },
  { chave: 'bonusPerLevel', icone: 'chart-line' },
  { chave: 'purchases', icone: 'hand-holding-usd' },
];

export default {
  name: 'HomePageComponent',
  components: {
    ShareButton,
    GameAlert,
    FishButton,
    FishModal,
    FishIcon,
    FishScene,
    LinhaDeMissao,
    PremioDasMissoes
  },
  setup() {
    const { t } = useI18n({ inheritLocale: true, useScope: 'global' });
    return { t };
  },
  data() {
    return {
      alertConfig: { message: '', type: 'info' },
      garrafaAberta: false,
      /** O ícone do botão de copiar vira o "check" por dois segundos: a confirmação fica onde o dedo está. */
      linkCopiado: false,
      bonusDaGarrafa: BONUS_DA_GARRAFA,
      /** O recado aberto no modal do "ler tudo". */
      recadoLido: null,
      leituraAberta: false
    };
  },
  computed: {
    ...mapState('player', {
      playerId: state => state.id,
      playerLevel: state => state.level,
      playerIdentifier: state => state.identifier,
      stamina: state => state.stamina
    }),
    ...mapState('announcements', ['announcements', 'error', 'loading']),
    ...mapState('dailyMissions', ['missions']),
    ...mapGetters('dailyMissions', ['rewardFormatted']),
    hasAnnouncements() {
      return Array.isArray(this.announcements) && this.announcements.length > 0;
    },
    missoesFeitas() {
      return this.missions.filter(m => m.isComplete).length;
    },
    temEnergia() {
      return (this.stamina?.current ?? 0) > 0;
    },
    shareUrl() {
      return `${window.location.origin}/refer/${this.playerIdentifier}`;
    }
  },
  methods: {
    ...mapActions('announcements', ['fetchAnnouncements']),
    /** O quadro de Missões Diárias mora no App.vue (o botão flutuante também o abre); a tábua pede por evento. */
    abrirQuadroDeMissoes() {
      window.dispatchEvent(new CustomEvent('abrir-missoes'));
    },
    /** O recado merece o "ler tudo"? A régua é o corte de seis linhas — ~320 caracteres o enchem. */
    recadoLongo(announcement) {
      return (announcement.description || '').length > 320;
    },
    lerRecado(announcement) {
      this.recadoLido = announcement;
      this.leituraAberta = true;
    },
    acaoPrincipal() {
      this.$router.push(this.temEnergia ? '/fishing' : '/house-rest');
    },
    copyToClipboard() {
      this.$refs.shareInput.select();
      navigator.clipboard.writeText(this.shareUrl);
      this.showAlert(this.$t('home.alert.linkCopied'), 'success');
      this.linkCopiado = true;
      clearTimeout(this.relogioDoCopiado);
      this.relogioDoCopiado = setTimeout(() => { this.linkCopiado = false; }, 2000);
    },
    showAlert(message, type = 'info') {
      this.alertConfig = { message, type };
      setTimeout(() => this.closeAlert(), 3000);
    },
    closeAlert() {
      this.alertConfig.message = '';
    },
    // O corpo vive em `@/utils/announcementText` porque o jest deste repositório não transforma
    // `.vue`: dentro do SFC o escape de HTML não teria como ser testado.
    formatDescription: formatarDescricao,
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('pt-BR', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  },
  async created() {
    await this.fetchAnnouncements();
  },
  beforeUnmount() {
    clearTimeout(this.relogioDoCopiado);
  }
}
</script>

<style scoped>
/* O cenário do Cais é a cena de água em PARALLAX (pedido do autor, 02/09: "mude o background da
 * home para aqueles com parallax"). Das oito cenas, `agua-doce` é a que tem VIDA na altura em que
 * os painéis flutuam — peixes nadando, pedra e coral embaixo; recife-raso e mar-aberto rendem só
 * faixas de luz a 1080p (testadas em screenshot e recusadas). A vila estática ficou no git. */
.cais {
  min-height: 100vh;
  /* espaço para a ação fixa + a barra de navegação */
  padding-bottom: 172px;
}

.cais__interior {
  position: relative;
  max-width: 720px;
  margin: 0 auto;
  padding: var(--e-5) var(--e-4) 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-5);
}

.cais__topo {
  text-align: center;
}

/* O título mora no PERGAMINHO do Paper Pack (`.banner-titulo`, papel.css) — tinta sobre papel,
 * sem o contorno de sobrevivência que o texto solto na água precisava. */
.cais__titulo {
  font-family: var(--fonte-display);
  font-size: var(--f-5);
  line-height: var(--lh-curto);
  margin: 0;
}

.cais__saudacao {
  font-family: var(--fonte-corpo);
  font-size: var(--f-3);
  color: var(--papel-luz);
  margin: var(--e-1) 0 0;
  text-shadow: 1px 1px 0 var(--agua-abissal);
}

.cais__rotulo {
  font-family: var(--fonte-display);
  font-size: var(--f-3);
  line-height: var(--lh-curto);
  margin: 0 0 var(--e-2);
  color: var(--tinta);
}

.cais__rotulo--claro {
  color: var(--papel-luz);
}

/* --- A tábua de missões: papel sobre a madeira do cais ---------------------------------------- */

/* A tábua de missões é o BLOCO DE ESPIRAL do Paper Pack (`.papel-bloco`) — um quadro de anotação
 * do dia, com as argolas no topo. O material vem de papel.css; aqui só o que é da tela. */
.cais__tabua {
  --folha: 560px;
  max-width: 560px;
  margin: 0 auto;
  width: 100%;
}

/* O título e o placar do dia inteiro na mesma linha: "Missões do dia ··· 0/7". */
.cais__tabua-topo {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--e-2);
  margin-bottom: var(--e-2);
}

.cais__tabua-topo .cais__rotulo {
  margin: 0;
}

.cais__tabua-placar {
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

/* As linhas são as do quadro (LinhaDeMissao); aqui só o espaçamento da lista. */
.cais__missoes {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

/* O prêmio fecha a tábua, com o fio tracejado do caderno — o mesmo do quadro. */
.cais__tabua-premio {
  margin-top: var(--e-3);
  padding-top: var(--e-2);
  border-top: 2px dashed var(--papel-sombra);
}

/* A porta para o quadro (e o resgate) — link, não botão: o `primario` da tela é o Pescar (D6). */
.cais__tabua-link {
  display: block;
  margin: var(--e-3) auto 0;
  padding: var(--e-1) var(--e-2);
  background: none;
  border: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--latao);
  text-decoration: underline;
  cursor: pointer;
}

/* --- O mural: madeira, com recados de papel pregados ------------------------------------------ */

.cais__mural {
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-madeira.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--madeira);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-3);
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.3);
}

.cais__mural-estado {
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  color: var(--papel-luz);
  margin: 0;
  text-align: center;
}

.cais__recados {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

/* Cada recado é uma folha pregada: papel, uma leve inclinação diferente por item, e a "tacha" de
 * latão em cima. Três recados retos e alinhados seriam um feed; tortos, são um mural. */
/* O recado do mural é a FOLHA EMPILHADA do Paper Pack: uma folha com outra torta por baixo — o
 * empilhado já faz o serviço que a rotação por CSS fazia, e melhor. */
.cais__recado {
  position: relative;
  background-image: url('/ui/papel-empilhado.png');
  background-size: 100% 100%;
  background-repeat: no-repeat;
  image-rendering: pixelated;
  color: var(--tinta);
  /* Em PORCENTAGEM: a arte estica junto com o recado, e o respiro tem que esticar igual — com
   * padding em px o texto vazava da folha de cima para a de baixo (visto em screenshot). */
  padding: 8% 14% 7%;
  transform: rotate(var(--gira, 0deg));
}

.cais__recado::before {
  content: '';
  position: absolute;
  top: -5px;
  left: 50%;
  width: 10px;
  height: 10px;
  transform: translateX(-50%);
  background: var(--ouro);
  border: 2px solid var(--ouro-escuro);
  border-radius: var(--r-circulo);
}

.cais__recado-topo {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: var(--e-2);
  margin-bottom: var(--e-1);
}

.cais__recado-titulo {
  font-family: var(--fonte-display);
  font-size: var(--f-3);
  line-height: var(--lh-curto);
  margin: 0;
}

.cais__recado-data {
  font-family: var(--fonte-corpo);
  font-size: var(--f-1);
  color: var(--tinta-lapis);
  white-space: nowrap;
}

.cais__recado-corpo {
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  line-height: var(--lh-prosa);
  /* Recado de mural é um AVISO, não um artigo: seis linhas e o resto atrás do "ler tudo". Sem o
   * corte, um anúncio longo do CMS vira a tela inteira — aconteceu no primeiro screenshot. */
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cais__recado-corpo :deep(.inline-link),
.leitura__corpo :deep(.inline-link),
.leitura__link,
.cais__recado-link {
  color: var(--latao);
  text-decoration: underline;
}

.cais__recado-link {
  display: inline-block;
  margin-top: var(--e-1);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
}

/* --- O recado inteiro, no pergaminho ---------------------------------------------------------- */

.leitura__data {
  display: block;
  font-family: var(--fonte-corpo);
  font-size: var(--f-1);
  color: var(--tinta-lapis);
  margin-bottom: var(--e-2);
}

/* 60dvh deixa sobrar, dentro dos 90dvh da caixa, o título com o "X" e o botão de fechar: os dois
 * ficam à vista do começo ao fim da leitura. Sem fio embaixo: o rodapé do FishFrame já traz o dele. */
.leitura__corpo {
  max-height: 60vh;
  max-height: 60dvh;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: var(--e-2);
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  line-height: var(--lh-prosa);
  color: var(--tinta);
  text-align: left;
  white-space: normal;
}

.leitura__link {
  display: inline-block;
  margin-top: var(--e-2);
  font-family: var(--fonte-corpo);
  font-size: var(--f-1);
  word-break: break-all;
}

/* --- A garrafa: o convite, com papel embaixo -------------------------------------------------- */

/* No telefone a garrafa e o texto dividem a primeira linha e o botão ocupa a segunda inteira —
 * é o alvo maior possível numa largura de 360px. A partir do tablet os três cabem lado a lado. */
.cais__convite {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: var(--e-3);
  padding: var(--e-3) var(--e-4);
  background: var(--papel-luz);
  border: 3px solid var(--latao);
  border-radius: var(--r-2);
  /* O card usa `.repouso-brilha`, que é como a novidade se anuncia no Fishbay (D0): o brilho
   * vai e volta em quatro segundos e o mínimo nunca é zero — presença, não alarme. Ele SUBSTITUI
   * esta `box-shadow` enquanto roda; a declaração fica porque é ela que sobra sob
   * `prefers-reduced-motion`, e sem ela o card ficaria chapado para quem pede menos movimento. */
  box-shadow: var(--sombra-2), var(--brilho-suave);
}

@media (min-width: 600px) {
  .cais__convite {
    grid-template-columns: auto 1fr auto;
  }
}

/* 64px é 2× do sprite de 32 — múltiplo exato, que é o que impede o borrão do 48px de antes. */
.cais__convite-selo {
  display: grid;
  place-items: center;
}

/* O `#app` centraliza o jogo inteiro (App.vue). Prosa centralizada com duas linhas fica
 * ragged dos dois lados e custa a leitura — aqui ela volta a alinhar à esquerda, ao lado da
 * garrafa. Dentro do modal isso não é preciso: o Teleport o leva para fora do `#app`. */
.cais__convite-texto {
  min-width: 0;
  text-align: left;
}

/* A chamada é a LEGENDA, não o título: quem lê primeiro tem que ler o que ganha. "Jogue uma
 * garrafa ao mar" é o sabor do cais (D13) e fica por cima, pequeno, em latão. */
.cais__convite-chamada {
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  line-height: var(--lh-curto);
  color: var(--latao);
  margin: 0 0 var(--e-1);
}

.cais__convite-ganho {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--e-1) var(--e-2);
  margin: 0;
}

/* O número é o ímã do olho, e por isso é do tamanho de um título de tela. Em `--parallelium`
 * porque é a moeda que ele paga — e a 32px o roxo passa no contraste de texto grande sobre o
 * papel claro, o que ele não faria no corpo do texto. */
.cais__convite-numero {
  font-family: var(--fonte-display);
  font-size: var(--f-6);
  line-height: var(--lh-cravado);
  font-variant-numeric: tabular-nums;
  color: var(--parallelium);
}

/* A unidade acompanha o número, e por isso é de APOIO: a 16px ela empurrava o "joga" sozinho
 * para uma terceira linha em 430px. */
.cais__convite-unidade {
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  line-height: var(--lh-curto);
  color: var(--tinta);
}

/* A conta, para o número não ser só uma promessa: quem quiser conferir, confere. */
.cais__convite-regra {
  font-family: var(--fonte-corpo);
  font-size: var(--f-1);
  line-height: var(--lh-normal);
  color: var(--tinta-fraca);
  margin: var(--e-1) 0 0;
}

.cais__convite-acao {
  grid-column: 1 / -1;
}

@media (min-width: 600px) {
  .cais__convite-acao {
    grid-column: auto;
  }
}

/* --- Redes sociais: uma linha discreta no pé, não cards na porta (D13) ------------------------ */

.cais__social {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--e-3);
  padding-bottom: var(--e-3);
}

.cais__social-rotulo {
  font-family: var(--fonte-corpo);
  font-size: var(--f-1);
  color: var(--texto-fraco);
  text-shadow: 1px 1px 0 var(--agua-abissal);
}

.cais__social a {
  color: var(--papel-sombra);
  font-size: var(--f-4);
  line-height: 1;
  text-shadow: 1px 1px 0 var(--agua-abissal);
  transition: color var(--t-rapido) var(--curva-suave);
}

@media (hover: hover) {
  .cais__social a:hover {
    color: var(--papel-luz);
  }
}

/* --- A ação, fixa acima da barra de navegação (D11) ------------------------------------------- */

.cais__acao {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(100px + env(safe-area-inset-bottom, 0px));
  width: min(420px, calc(100vw - 2 * var(--e-4)));
  z-index: var(--z-flutuante, 50);
}

/* --- A garrafa por dentro --------------------------------------------------------------------- */

.garrafa__bonus {
  list-style: none;
  margin: 0 0 var(--e-4);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

/* A ficha: papel sobre papel, com o fio de latão à esquerda para dar a borda de leitura. A sombra
 * é a CHAPADA do sistema — pixel art não faz sombra difusa, ela desce. */
.garrafa__ficha {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  padding: var(--e-2) var(--e-3);
  background: var(--papel-luz);
  border: 2px solid var(--papel-sombra);
  border-left: 4px solid var(--latao);
  border-radius: var(--r-2);
  box-shadow: var(--sombra-1);
  transition: transform var(--t-rapido) var(--curva-suave),
    border-color var(--t-rapido) var(--curva-suave);
}

@media (hover: hover) {
  .garrafa__ficha:hover {
    transform: translateX(2px);
    border-color: var(--latao);
  }
}

/* O selo é BURACO na página: o inset é o que faz o ícone parecer carimbado e não colado. 32px é
 * o 1:1 nativo do sprite, e a caixa é 48 para o desenho não encostar no fio. */
.garrafa__selo {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  background: var(--papel-mancha);
  border: 2px solid var(--latao);
  border-radius: var(--r-2);
  box-shadow: var(--sombra-afundada);
}

.garrafa__ficha-texto {
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  min-width: 0;
}

.garrafa__ficha-titulo {
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  line-height: var(--lh-curto);
  color: var(--tinta);
}

.garrafa__ficha-desc {
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  line-height: var(--lh-normal);
  color: var(--tinta-fraca);
}

/* O prêmio é o que o olho procura. Fica em latão e na fonte de display — e `nowrap` porque
 * "20 Paralleliums" quebrado ao meio deixa de ser um número. Latão, não parallelium: o roxo do
 * token não tem contraste suficiente sobre o papel claro. */
.garrafa__bonus :deep(.highlight) {
  font-family: var(--fonte-display);
  color: var(--latao);
  font-weight: 700;
  white-space: nowrap;
}

/* O fio tracejado é o do caderno; a promessa acaba nele e a ação começa. */
.garrafa__partilha {
  padding-top: var(--e-3);
  border-top: 2px dashed var(--papel-sombra);
}

.garrafa__info {
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  margin: 0 0 var(--e-2);
}

.garrafa__linha {
  display: flex;
  align-items: stretch;
  gap: var(--e-2);
  margin-bottom: var(--e-3);
}

/* O campo é AFUNDADO: é o que se lê e se copia, não uma superfície que se levanta. */
.garrafa__link {
  flex: 1;
  min-width: 0;
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  padding: var(--e-2) var(--e-3);
  border: 2px solid var(--papel-sombra);
  border-radius: var(--r-2);
  background: var(--papel-mancha);
  box-shadow: var(--sombra-afundada);
  color: var(--tinta);
  text-overflow: ellipsis;
  cursor: pointer;
  transition: border-color var(--t-rapido) var(--curva-suave);
}

@media (hover: hover) {
  .garrafa__link:hover {
    border-color: var(--latao);
  }
}

.garrafa__partilha :deep(.share-container) {
  display: block;
}
</style>
